import { Divider, StickyScrollPageHeader } from "@clipboard-health/ui-components";
import { getSafeAreaPaddings } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack, type Theme } from "@mui/material";
import { NotFoundPage } from "@src/appV2/Errors/NotFound/Page";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { useTier } from "@src/appV2/redesign/Tiers/api/useTier";
import { HeaderSection } from "@src/appV2/redesign/Tiers/HeaderSection/HeaderSection";
import { LoadingPage } from "@src/appV2/redesign/Tiers/LoadingPage";
import { PerksSection } from "@src/appV2/redesign/Tiers/PerksSection/PerksSection";
import { RatingsSection } from "@src/appV2/redesign/Tiers/RatingsSection/RatingsSection";
import { DEFAULT_LATE_CANCELLATION_THRESHOLD_IN_HOURS } from "@src/appV2/redesign/Tiers/utils/constants";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { parseISO } from "date-fns";
import { Link, useHistory } from "react-router-dom";

export function TiersPage() {
  const useTierResponse = useTier();
  const { enabled, successfullyLoadedTier, isLoading } = useTierResponse;
  const history = useHistory();
  const worker = useDefinedWorker();

  useLogEffect(APP_V2_USER_EVENTS.TIERS_MEMBERSHIP_PAGE_VIEWED, { workerId: worker.userId });

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!successfullyLoadedTier || !enabled) {
    return <NotFoundPage />;
  }

  const {
    tier,
    tierConfiguration: { lateCancellationThresholdInMinutes },
    normalizedTierRatings,
    workerTimeZone,
  } = useTierResponse;
  const {
    attributes: { type: tierType, tierLastChangedAt },
  } = tier;

  const lateCancelThresholdInHours = isDefined(lateCancellationThresholdInMinutes)
    ? lateCancellationThresholdInMinutes / 60
    : DEFAULT_LATE_CANCELLATION_THRESHOLD_IN_HOURS;

  return (
    <PageWrapper
      sx={(theme: Theme) => ({
        backgroundColor: theme.palette.background.tertiary,
        overflow: "auto",
        ...getSafeAreaPaddings({ bottom: true }),
      })}
    >
      <StickyScrollPageHeader
        title=""
        headerVariant="tertiary"
        navigateBack={() => {
          history.goBack();
        }}
        LinkComponent={Link}
      />
      <PageContent>
        <Stack direction="column" gap={8}>
          <HeaderSection workerTier={tierType} tierLastChangedAt={parseISO(tierLastChangedAt)} />
          <RatingsSection
            workerTimeZone={workerTimeZone}
            normalizedTierRatings={normalizedTierRatings}
            lateCancelThresholdInHours={lateCancelThresholdInHours}
            workerId={worker.userId}
          />
          <Divider />
          <PerksSection workerTier={tierType} />
        </Stack>
      </PageContent>
    </PageWrapper>
  );
}
